import { IDisclosure, IParticipatingOrganizationDto, IParticipatingStudyDto } from '@vivli/features/disclosures/infrastructure/interface';
import { DisclosureContext } from '@vivli/features/disclosures/infrastructure/context';
import { PageView } from '@vivli/shared/views';
import { IPageLink, IUser } from '@vivli/shared/infrastructure/interface';
import { useDisclosurePermissions, useDisclosureStatus } from '@vivli/features/disclosures/infrastructure/hook';
import { DisclosureInformationFormView, DocumentsForReviewFormView, FeedbackFormView, StudiesUsedFormView } from '../forms';
import { DisclosureHistoryView } from '../disclosure-history.view';
import { DisclosureHeaderView } from '../disclosure-header.view';
import { DisclosureStatusEnum } from '@vivli/features/disclosures/infrastructure/enum';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { concat } from 'rxjs';
import { useActiveUserContext, useToastApiContext } from '@vivli/core/infrastructure/context';
import { useDefaultLink } from '@vivli/shared/infrastructure/hook';
import { concatMap, finalize, last } from 'rxjs/operators';
import { CitationsView } from '@vivli/shared/features/citations/views';
import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { DisclosureViewProps } from '@vivli/features/disclosures/views';

const msgStyle = {
    display: 'grid',
    gridTemplateRows: 'auto auto auto',
    rowGap: '20px'
};

export const DisclosureFormView = ({
    disclosure,
    user,
    onBackClick,
    onSave,
    onSubmit,
    onChangeStatus,
    onReviewFinalized,
    dataRequest,
    defaultTab,
    onLinkClick,
    ...props
}: DisclosureViewProps): JSX.Element => {
    const { watch, getValues, reset, trigger, handleSubmit } = useFormContext<IDisclosure>();
    const { isDirty } = useFormState();
    const _modalService = useModalApiContext();
    const _toastService = useToastApiContext();
    const getDisclosureStatus = useDisclosureStatus();
    const { hasPermission } = useActiveUserContext();

    const status = watch('status');
    const disclosureId = watch('id');

    const [isLoading, setIsLoading] = useState(false);

    const permissions = useDisclosurePermissions(disclosure, dataRequest, user);

    const getStudiesForOrg = (orgId: string) => {
        const participatingStudies = getValues()
            .studyDisclosureDetails.filter((sdd) => sdd.organizationId === orgId)
            .map<IParticipatingStudyDto>((sdd) => {
                const study = dataRequest.requestedStudies.find((rs) => rs.studyId === sdd.studyId);

                return {
                    studyId: sdd.studyId,
                    studyTitle: study?.studyTitle
                };
            });

        return participatingStudies;
    };

    const getPageLinks = () => {
        const defaultPageLink: IPageLink = {
            title: 'Disclosure Information',
            url: 'disclosure-information',
            component: <DisclosureInformationFormView />
        };

        return [
            defaultPageLink,
            {
                title: 'Studies Used',
                url: 'studies-used',
                component: <StudiesUsedFormView />
            },
            {
                title: 'Documents for Review',
                url: 'documents-for-review',
                component: <DocumentsForReviewFormView />
            },
            {
                title: 'Feedback / Attachments',
                url: 'feedback-and-attachments',
                component: <FeedbackFormView />,
                hidden: status === DisclosureStatusEnum.Draft
            },
            {
                title: 'Citations',
                url: 'citations',
                component: <CitationsView />,
                hidden: permissions.hideCitations
            },
            // saving this for future use if we implement this req
            // {
            //     title: 'Chat',
            //     url: 'chat',
            //     component: <DisclosureChatView />,
            // },
            {
                title: 'History',
                url: 'history',
                component: <DisclosureHistoryView />
            }
        ];
    };

    const handleDisclosureUpdated = (updatedDisclosure: IDisclosure) => {
        reset(updatedDisclosure, { keepErrors: true });
    };

    const _handleChangeStatus = (status: DisclosureStatusEnum, successMsg: string, errorMsg: string) => {
        if (!onChangeStatus) {
            return;
        }

        setIsLoading(true);
        onChangeStatus(getValues(), status)
            .pipe(finalize(() => setIsLoading(false)))
            .subscribe({
                next: (updatedDisclosure) => {
                    handleDisclosureUpdated(updatedDisclosure);
                    _toastService.success(successMsg);
                },
                error: (error) => {
                    _toastService.error(errorMsg);
                }
            });
    };

    const onReturnToDraftClick = () => {
        _handleChangeStatus(
            DisclosureStatusEnum.Draft,
            'Disclosure Returned to Draft.',
            'An error has occured while returning your disclosure to draft.'
        );
    };

    const onPublishedClick = () => {
        _handleChangeStatus(
            DisclosureStatusEnum.Published,
            'Disclosure set to Published.',
            'An error has occured while setting your disclosure to Published.'
        );
    };

    const onNotPublishedClick = () => {
        _handleChangeStatus(
            DisclosureStatusEnum.NotPublished,
            'Disclosure set to Not Published.',
            'An error has occured while setting your disclosure to Not Published.'
        );
    };

    const handleSave = (onSuccess?: () => void) => {
        setIsLoading(true);
        onSave(getValues())
            .pipe(finalize(() => setIsLoading(false)))
            .subscribe({
                next: (updatedDisclosure) => {
                    handleDisclosureUpdated(updatedDisclosure);
                    _toastService.success('Disclosure Saved.');
                    onSuccess && onSuccess();
                },
                error: (error) => {
                    _toastService.error('An error has occured while saving your disclosure.');
                }
            });
    };

    const onSaveClick = () => {
        if (!onSave || !isDirty) {
            return;
        }

        handleSave();
    };

    const onSubmitClick = () => {
        if (!onSubmit) {
            return;
        }

        handleSubmit((disclosure) => {
            setIsLoading(true);
            onSubmit(disclosure)
                .pipe(finalize(() => setIsLoading(false)))
                .subscribe({
                    next: (updatedDisclosure) => {
                        handleDisclosureUpdated(updatedDisclosure);
                        _toastService.success('Disclosure Submitted Successfully.');
                    },
                    error: (error) => {
                        _toastService.error('An error has occured while submitting your disclosure.');
                    }
                });
        })();
    };

    const onFinalizedAllClick = (participatingOrgs: IParticipatingOrganizationDto[]) => {
        if (!onReviewFinalized) {
            return;
        }

        _modalService.confirm(null, {
            title: 'Mark Review Completed for All Participating Orgs',
            style: { textAlign: 'left', minWidth: '50em' },
            messageStyle: { textAlign: 'left' },
            showTextAreaPrompt: true,
            promptLabel: 'Comments',
            confirmText: 'OK',
            cancelText: 'Cancel',
            onConfirm: ({ comment }) => {
                setIsLoading(true);

                const apiCalls = participatingOrgs.map((org) => {
                    const participatingStudies = getStudiesForOrg(org.orgId);

                    return onReviewFinalized({
                        ...org,
                        feedbackComment: comment,
                        participatingStudies
                    });
                });

                concat(apiCalls)
                    .pipe(
                        concatMap((x) => x),
                        last()
                    )
                    .subscribe({
                        next: (updatedDisclosure) => {
                            handleDisclosureUpdated(updatedDisclosure);
                            setIsLoading(false);
                        }
                    });
            }
        });
    };

    const onFinalizedClick = (org: IParticipatingOrganizationDto) => {
        if (!onReviewFinalized) {
            return;
        }

        const message = (
            <div style={msgStyle}>
                <div>
                    You are about to confirm the completion of the 30 day review. Once you click “OK”, your action is complete, and you
                    cannot go back to the previous step.
                </div>
                <div>
                    If you have feedback in the form of an attachment, please click “Cancel” and upload the document in the
                    feedback/attachments tab before proceeding further.
                </div>
                <div>If you have feedback in the form of text, you may type in your comment in the Comments box below and click “OK".</div>
            </div>
        );

        _modalService.confirm(message, {
            title: 'Mark Review Completed',
            style: { textAlign: 'left' },
            messageStyle: { textAlign: 'left' },
            showTextAreaPrompt: true,
            promptLabel: 'Comments',
            confirmText: 'OK',
            cancelText: 'Cancel',
            onConfirm: ({ comment }) => {
                let participatingOrg = disclosure.contributingOrgDetails?.find((o) =>
                    user.orgMemberships.some((x) => x.orgId === o.organizationId)
                );

                // used when a Vivli Admin is approving on behalf of an organization
                if (!participatingOrg || hasPermission(PermissionsEnum.DrActAdv)) {
                    participatingOrg = disclosure.contributingOrgDetails?.find((o) => o.organizationId === org.orgId);
                }

                const participatingStudies = getStudiesForOrg(participatingOrg.organizationId);

                const orgDto: IParticipatingOrganizationDto = {
                    organizationName: participatingOrg.organizationName,
                    orgId: participatingOrg.organizationId,
                    finalizedReview: true,
                    feedbackComment: comment,
                    uploadedFiles: [],
                    participatingStudies
                };

                setIsLoading(true);
                onReviewFinalized({
                    ...orgDto,
                    participatingStudies
                }).subscribe({
                    next: (updatedDisclosure) => {
                        handleDisclosureUpdated(updatedDisclosure);
                        setIsLoading(false);
                    }
                });
            }
        });
    };

    const pageTitle = useMemo(() => `Public Disclosure ${disclosure?.vivliDisclosureId}`, [disclosureId, dataRequest.doiStem]);

    const pageLinks = useMemo(() => getPageLinks(), [status]);

    const statusDescription = useMemo(() => getDisclosureStatus(disclosure), [status, disclosure.status]);

    const defaultLink = useDefaultLink(pageLinks, defaultTab);

    useEffect(() => {
        // trigger the form for initial validation, ie show red for required fields
        trigger();
    }, []);

    return (
        <DisclosureContext.Provider
            value={{
                permissions,
                disclosure,
                user,
                dataRequest,
                onSaveClick,
                onReturnToDraftClick,
                onPublishedClick,
                onNotPublishedClick,
                onSubmitClick,
                onFinalizedClick,
                onFinalizedAllClick,
                ...props
            }}
        >
            <PageView
                title={pageTitle}
                subTitle={`Status: ${statusDescription}`}
                links={pageLinks}
                headerContent={<DisclosureHeaderView />}
                defaultLink={defaultLink}
                onTabClick={(link) => {
                    // handle auto-save if form has changes or if it has never been saved
                    if (isDirty || disclosureId === 0) {
                        handleSave(() => {
                            onLinkClick(link);
                        });
                    } else {
                        onLinkClick(link);
                    }
                }}
                onBackClick={onBackClick}
                isLoading={isLoading}
            />
        </DisclosureContext.Provider>
    );
};
